import React from 'react';
import { ReactComponent as JiraIcon } from '@elementary/common-ui/assets/16x16/jira.svg';
import { ReactComponent as LinearIcon } from '@elementary/common-ui/assets/16x16/linear.svg';
import { ReactComponent as ServiceNowIcon } from '@elementary/common-ui/assets/16x16/servicenow.svg';
import { useTheme } from '@mui/material';
import { TicketingTool } from 'shared/types';
import multipleDestinationsRenderer from '../../shared/multipleDestinationsRenderer';
import { DialogTypes } from './types';

export const platformIconMap: Record<string, React.ReactNode> = {
  servicenow: <ServiceNowIcon />,
  jira: <JiraIcon />,
  linear: <LinearIcon />,
};

export type TicketingIntegrationDetails = {
  id: string;
  type: TicketingTool;
  name: string;
  enabled: boolean;
};

export type TicketingMultipleDestinationsProps = {
  ticketingIntegrations: TicketingIntegrationDetails[];
  setSelectedTicketingIntegrationId: (id: string | null) => void;
  setDialog: (dialog: DialogTypes) => void;
};

const TicketingMultipleDestinations = ({
  ticketingIntegrations,
  setSelectedTicketingIntegrationId,
  setDialog,
}: TicketingMultipleDestinationsProps): React.ReactNode => {
  const theme = useTheme();
  const handleChange = (method: string, id: string) => {
    setSelectedTicketingIntegrationId(id);

    if (method === 'update') {
      setDialog('updateTicketingTool');
    } else if (method === 'delete') {
      setDialog('deleteTicketingTool');
    } else if (method === 'reauthenticate') {
      const integration = ticketingIntegrations.find((t) => t.id === id);
      if (integration?.type === 'jira') {
        setDialog('jiraReauth');
      } else if (integration?.type === 'linear') {
        setDialog('linearReauth');
      }
    } else if (method === 'webhook') {
      setDialog('jiraWebhook');
    }
  };

  return multipleDestinationsRenderer({
    integrations: ticketingIntegrations.map((ticketingIntegration) => ({
      id: ticketingIntegration.id,
      icon: platformIconMap[ticketingIntegration.type],
      name: ticketingIntegration.name,
      status: ticketingIntegration.enabled ? 'connected' : 'error',
      customOptions:
        ticketingIntegration.type === 'jira'
          ? [
              { value: 'update', label: 'Edit connection' },
              { value: 'webhook', label: 'Configure webhook' },
              { value: 'reauthenticate', label: 'Re-authenticate' },
              {
                value: 'delete',
                label: 'Delete connection',
                color: theme.palette.status.error.main,
              },
            ]
          : ticketingIntegration.type === 'linear'
          ? [
              { value: 'update', label: 'Edit connection' },
              { value: 'reauthenticate', label: 'Re-authenticate' },
              {
                value: 'delete',
                label: 'Delete connection',
                color: theme.palette.status.error.main,
              },
            ]
          : undefined,
    })),
    onChange: handleChange,
    options: [
      { value: 'update', label: 'Edit connection' },
      {
        value: 'delete',
        label: 'Delete connection',
        color: theme.palette.status.error.main,
      },
    ],
  });
};

export default TicketingMultipleDestinations;
